import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdClose } from "react-icons/md";
import { FaTrain, FaClock, FaDollarSign } from "react-icons/fa";

const TrainResultsModal = ({ isOpen, onClose, onSelect, searchData }) => {
  const [loading, setLoading] = useState(true);
  const [trains, setTrains] = useState([]);

  const mockTrains = [
    {
      id: 1,
      name: "Express 2001",
      type: "High Speed Express",
      route: `${searchData?.from || 'New York'} → ${searchData?.to || 'Boston'}`,
      time: "6:00 AM - 10:30 AM",
      duration: "4h 30m",
      price: "$89",
      departureDate: searchData?.departureDate || "2024-01-15"
    },
    {
      id: 2,
      name: "Regional 1205", 
      type: "Regional Service",
      route: `${searchData?.from || 'New York'} → ${searchData?.to || 'Boston'}`,
      time: "9:15 AM - 2:45 PM",
      duration: "5h 30m",
      price: "$65",
      departureDate: searchData?.departureDate || "2024-01-15"
    },
    {
      id: 3,
      name: "Acela 2150",
      type: "Premium Express", 
      route: `${searchData?.from || 'New York'} → ${searchData?.to || 'Boston'}`,
      time: "12:00 PM - 3:30 PM",
      duration: "3h 30m",
      price: "$149",
      departureDate: searchData?.departureDate || "2024-01-15"
    },
    {
      id: 4,
      name: "Northeast 448",
      type: "Standard Service",
      route: `${searchData?.from || 'New York'} → ${searchData?.to || 'Boston'}`,
      time: "4:20 PM - 9:15 PM", 
      duration: "4h 55m",
      price: "$55",
      departureDate: searchData?.departureDate || "2024-01-15"
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setTrains(mockTrains);
    }
  }, [isOpen]);

  const handleSelect = (train) => {
    onSelect(train);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <Card extra="w-[800px] max-h-[90vh] overflow-hidden bg-white/95 dark:bg-navy-800/95 backdrop-blur-sm border border-white/20 dark:border-navy-600/50 shadow-2xl">
        <div className="bg-[#5a88c6] shadow-lg p-6 rounded-t-[20px] relative overflow-hidden sticky top-0 z-20">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-2xl bg-white/20 backdrop-blur-sm p-3">
                <FaTrain className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Available Trains</h3>
                <p className="text-white/80 text-sm">
                  {searchData?.from} → {searchData?.to} • {searchData?.departureDate}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30 transition-colors">
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400 text-lg font-medium">Searching for trains...</p>
              <p className="text-slate-500 dark:text-slate-500 text-sm">Please wait while we find the best options</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-700 dark:text-white font-semibold mb-4">
                Found {trains.length} trains for your search
              </p>
              {trains.map((train) => (
                <div
                  key={train.id}
                  className="group p-6 rounded-2xl bg-gradient-to-r from-white to-slate-50 dark:from-navy-800 dark:to-navy-700 border-2 border-slate-200 dark:border-navy-600 hover:border-blue-400 dark:hover:border-blue-400 hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => handleSelect(train)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-2xl bg-[#5a88c6] shadow-lg">
                        <FaTrain className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-800 dark:text-white">{train.name}</h4>
                        <p className="text-slate-600 dark:text-slate-400">{train.type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{train.price}</p>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">per person</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <FaTrain className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{train.route}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaClock className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{train.time}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-500 dark:text-slate-400">⏱️</span>
                      <span className="text-slate-700 dark:text-white font-medium">{train.duration}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <button className="px-6 py-2 bg-[#5a88c6] text-white rounded-xl font-semibold hover:bg-[#4a7bb6] transition-all duration-200 group-hover:scale-105">
                      Select Train
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TrainResultsModal;